import type { FetchCabinsRequestRedux } from '@/ducks/cabinsCommon/types';
import type { AppDispatch, AppGetState } from '@/store';

import { fetchCabinCategories } from '@/ducks/cabinsCommon/cabinCategories/api';
import { selectSailingData } from '@/ducks/voyageInfo/sailing/selectors';
import { clearAll, setWarningAnyCabinsExhausted, setWarningForPastVoyage } from '@/features/warnings/actions';
import {
  type TCabinCategoriesAvailabilityRequest,
  type TCabinCategory,
} from '@/helpers/api/graphql/queries/cabinCategoriesAvailability';
import { getFeatureFlagBool } from '@/helpers/feature-flag/api/getFeatureFlagBool';
import { FEATURE_FLAGS } from '@/helpers/feature-flag/model/constants';
import { cabinCategoryActions } from '@/reducers/voyagePlanner/chooseCabin/cabinCategories';
import { CABIN_CATEGORIES_ERROR_TYPES } from '@/reducers/voyagePlanner/chooseCabin/cabinCategories/constants';
import prepareCabinCategories from '@/reducers/voyagePlanner/chooseCabin/cabinCategories/prepareCabinCategories';

const checkForAllCabinExhaust = (cabinCategories?: TCabinCategory[]) =>
  cabinCategories?.some((cabin) => cabin.submetas.some((subMeta) => subMeta.isAvailable));

export const getCabinCategories =
  ({
    onExhaustError,
    onFetchError,
    payloadForCabinsFetch,
    voyageId,
  }: {
    onExhaustError?: () => void;
    onFetchError?: () => void;
    payloadForCabinsFetch: FetchCabinsRequestRedux;
    voyageId: string;
  }) =>
  async (dispatch: AppDispatch, getState: AppGetState) => {
    dispatch(clearAll());
    try {
      const isFeatureFareClassesEnabled = (await getFeatureFlagBool(FEATURE_FLAGS.FARE_CLASSES)).value;
      const payload: TCabinCategoriesAvailabilityRequest = {
        ...payloadForCabinsFetch,
        voyageId,
      };
      dispatch(cabinCategoryActions.setLoading(payload));
      const fetchResult = await fetchCabinCategories(payload, isFeatureFareClassesEnabled);
      if (fetchResult.error) {
        dispatch(
          cabinCategoryActions.setError({ message: fetchResult.error, type: CABIN_CATEGORIES_ERROR_TYPES.FETCH_ERROR }),
        );

        dispatch(setWarningForPastVoyage(true));
        onFetchError?.();
        return;
      }

      const areCabinsAvailable = checkForAllCabinExhaust(fetchResult.data);

      if (!areCabinsAvailable) {
        dispatch(cabinCategoryActions.setError({ type: CABIN_CATEGORIES_ERROR_TYPES.ALL_EXHAUSTED }));
        dispatch(setWarningAnyCabinsExhausted());
        onExhaustError?.();
        return;
      }

      const preparedData = prepareCabinCategories(
        structuredClone(fetchResult.data ?? []),
        selectSailingData(getState())?.ship?.code,
      );
      dispatch(cabinCategoryActions.setData(preparedData));
    } catch (error) {
      dispatch(
        cabinCategoryActions.setError({
          message: `${(error as Error)?.message}`,
          type: CABIN_CATEGORIES_ERROR_TYPES.UNKNOWN,
        }),
      );

      onFetchError?.();
    }
  };
